import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "@/lib/api";
import { TierGate } from "@/components/TierGate";
import { ExclusiveGate } from "@/components/ExclusiveGate";
import { ArrowLeft, Target, AlertTriangle, XCircle, Lightbulb } from "lucide-react";
import { convictionBadgeClass } from "@/lib/conviction";

function List({ title, items, icon: Icon, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${color}`} />
        <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-400">{title}</p>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 text-sm text-zinc-200">
            <span className={`mt-2 w-1 h-1 rounded-full shrink-0 ${color.replace("text-", "bg-")}`} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ConvictionDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setC(null); setErr(false);
    const load = () => client.get(`/conviction/${slug}`).then((r) => setC(r.data)).catch(() => setErr(true));
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-zinc-400">Thesis not found. <Link to="/conviction" className="text-[#ccff00]">Back</Link></div>;
  if (!c) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-zinc-500">Loading…</div>;

  const invalid = c.status === "INVALIDATED";
  const neg = (c.performance_value ?? 0) < 0;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <Link to="/conviction" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#ccff00] mb-8 transition-colors">
        <ArrowLeft className="w-4 h-4" /> All theses
      </Link>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="font-display text-5xl font-black">{c.asset}</span>
        <span className={`font-mono-cl text-[11px] tracking-[0.14em] uppercase px-3 py-1 rounded-full border ${convictionBadgeClass(c.status)}`}>
          {c.status}
        </span>
      </div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold">{c.title}</h1>

      {c.live && (
        <div data-testid="live-performance" className="mt-6 rounded-2xl border border-[#ccff00]/30 bg-[#ccff00]/[0.04] p-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono-cl text-[10px] tracking-[0.18em] uppercase text-[#ccff00] flex items-center gap-2 mb-1">
              <span className="live-dot w-1.5 h-1.5 rounded-full bg-[#ccff00]" /> Live Performance · {c.track_type === "stock" ? "US Stock" : "Crypto"}
            </p>
            <p className={`font-display text-5xl font-black ${neg ? "text-red-400" : "text-[#ccff00]"}`}>{c.performance}</p>
          </div>
          <div className="flex gap-8 font-mono-cl">
            <div>
              <p className="text-[10px] tracking-[0.16em] uppercase text-zinc-600">Entry ({c.track_symbol})</p>
              <p className="text-lg font-bold">${c.entry_price}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.16em] uppercase text-zinc-600">Live Price</p>
              <p className="text-lg font-bold text-[#ccff00]">${c.live_price}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 my-8">
        <div className="cl-card p-4" style={{ transform: "none" }}>
          <p className="font-mono-cl text-[10px] tracking-[0.14em] uppercase text-zinc-600">Time Horizon</p>
          <p className="font-semibold mt-1">{c.time_horizon}</p>
        </div>
        <div className="cl-card p-4" style={{ transform: "none" }}>
          <p className="font-mono-cl text-[10px] tracking-[0.14em] uppercase text-zinc-600">{c.live ? "Live · Since Research" : "Since Research"}</p>
          <p className={`font-mono-cl text-xl font-bold mt-1 ${neg ? "text-red-400" : "text-[#ccff00]"}`}>{c.performance || "-"}</p>
        </div>
        <div className="cl-card p-4" style={{ transform: "none" }}>
          <p className="font-mono-cl text-[10px] tracking-[0.14em] uppercase text-zinc-600">Published</p>
          <p className="font-semibold mt-1">{c.date_published ? new Date(c.date_published).toLocaleDateString() : "-"}</p>
        </div>
      </div>

      {c.locked ? (
        c.blur_preview ? (
          <ExclusiveGate preview={c.preview} analystName={c.analyst_name} analystSlug={c.analyst_slug} label="thesis" />
        ) : (
          <TierGate tier="member" />
        )
      ) : (
        <>
          <div className="rounded-2xl border border-white/10 p-6 mb-8">
            <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-[#ccff00] mb-3">Thesis</p>
            <p className="text-zinc-200 leading-relaxed text-lg">{c.thesis}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8 mb-8">
            <List title="Catalysts" items={c.catalysts} icon={Target} color="text-[#ccff00]" />
            <List title="Risks" items={c.risks} icon={AlertTriangle} color="text-amber-400" />
          </div>

          {c.invalidation && (
            <div className="rounded-2xl border border-white/10 p-6 mb-8">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-4 h-4 text-red-400" />
                <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-400">Invalidation Criteria</p>
              </div>
              <p className="text-zinc-200 leading-relaxed">{c.invalidation}</p>
            </div>
          )}

          {invalid && c.what_we_learned && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/[0.04] p-6 mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-4 h-4 text-red-400" />
                <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-red-400">What We Learned</p>
              </div>
              <p className="text-zinc-200 leading-relaxed">{c.what_we_learned}</p>
            </div>
          )}

          {/* Timeline */}
          {c.timeline && c.timeline.length > 0 && (
            <div className="my-10">
              <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-[#ccff00] mb-6">Thesis Timeline</p>
              <div className="relative pl-8">
                <div className="absolute left-[7px] top-1 bottom-1 w-px bg-white/10" />
                {c.timeline.map((t, i) => (
                  <div key={i} className="relative mb-7 last:mb-0">
                    <div className="absolute -left-8 top-1 w-3.5 h-3.5 rounded-full border-2 border-[#ccff00] bg-[#050505]" />
                    <p className="font-mono-cl text-xs text-[#ccff00] tracking-[0.14em]">{t.date}</p>
                    <p className="font-semibold mt-0.5">{t.label}</p>
                    {t.note && <p className="text-sm text-zinc-400 mt-0.5">{t.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
